import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Mic, Database, Mail, Workflow, ShieldCheck, LogOut, Plug, Volume2, Play, Loader2, Phone, Copy } from "lucide-react";
import { Logo } from "@/components/Logo";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import { useStore } from "@/lib/store";
import { API, twilio, voice as voiceApi } from "@/lib/api";
import { getAmbienceVolume, setAmbienceVolume } from "@/lib/sfx";
import { VOICE_OPTIONS, getVoiceFor, setVoiceFor } from "@/lib/realtime";
import { toast } from "sonner";

// Settings doubles as the map of FUTURE INTEGRATION POINTS.
const INTEGRATIONS = [
  { icon: Mic, name: "Real-time Voice AI", desc: "Connect OpenAI Realtime for live spoken conversations with associates.", status: "Ready to connect" },
  { icon: Database, name: "CRM & Records", desc: "Sync device, customer and service records from your systems.", status: "Ready to connect" },
  { icon: Mail, name: "Email & Messaging", desc: "Let associates send and receive approved communications.", status: "Ready to connect" },
  { icon: Workflow, name: "Workflow Engine", desc: "Trigger real actions when rules and approvals are met.", status: "Ready to connect" },
];

export default function Settings() {
  const navigate = useNavigate();
  const { state, reset } = useStore();
  const [volume, setVolume] = useState(() => getAmbienceVolume());
  const [voices, setVoices] = useState(() => {
    const m = {};
    (state.employees || []).forEach((e) => { m[e.id] = getVoiceFor(e.id); });
    return m;
  });
  const changeVoice = (id, v) => {
    setVoiceFor(id, v);
    setVoices((prev) => ({ ...prev, [id]: v }));
    voiceApi.setPref(id, v).catch(() => {}); // persist for the phone line (shared)
    const emp = (state.employees || []).find((e) => e.id === id);
    toast.success(`${emp ? emp.name : "Associate"} voice updated`, { description: "Heard on your next call with them — phone included." });
  };
  // Load shared voice prefs from the backend so the picker + phone stay in sync.
  useEffect(() => {
    voiceApi.prefs().then((d) => {
      const p = d.prefs || {};
      setVoices((prev) => ({ ...prev, ...p }));
      Object.entries(p).forEach(([id, v]) => setVoiceFor(id, v));
    }).catch(() => {});
  }, []);
  const [previewing, setPreviewing] = useState(null);
  const audioRef = useRef(null);
  const previewVoice = (id, voice) => {
    if (!audioRef.current) audioRef.current = new Audio();
    const el = audioRef.current;
    try { el.pause(); } catch (_) {}
    setPreviewing(id);
    el.src = `${API}/voice/sample?voice=${encodeURIComponent(voice)}`;
    el.onended = () => setPreviewing(null);
    el.onerror = () => { setPreviewing(null); toast.error("Couldn't play sample"); };
    const p = el.play();
    if (p && p.catch) p.catch(() => { setPreviewing(null); });
  };
  const [phone, setPhone] = useState(null);
  useEffect(() => { twilio.status().then(setPhone).catch(() => setPhone(null)); }, []);
  const webhookUrl = `${API}/twilio/voice`;
  const smsWebhookUrl = `${API}/twilio/sms`;
  const copy = (text) => { try { navigator.clipboard.writeText(text); toast.success("Copied"); } catch (_) {} };
  return (
    <div>
      <div className="mb-6">
        <h1 className="font-display text-3xl font-semibold text-white sm:text-4xl">Settings</h1>
        <p className="mt-1 text-sm text-slate-400">Workspace, integrations and controls</p>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="glass rounded-2xl p-5" data-testid="settings-workspace">
          <div className="mb-3 flex items-center gap-2 text-sm font-medium text-white"><Building2 size={16} className="text-[#00E5FF]" /> Workspace</div>
          <Logo size={44} withWordmark subtitle="IQ IA Solutions" />
          <div className="mt-4 space-y-2 text-sm text-slate-300">
            <div className="flex justify-between rounded-lg border border-white/8 bg-[#030A16]/40 px-3 py-2"><span className="text-slate-500">Company</span><span>ABC Services</span></div>
            <div className="flex justify-between rounded-lg border border-white/8 bg-[#030A16]/40 px-3 py-2"><span className="text-slate-500">Owner</span><span>Lewis (demo)</span></div>
            <div className="flex justify-between rounded-lg border border-white/8 bg-[#030A16]/40 px-3 py-2"><span className="text-slate-500">Plan</span><span>Prototype</span></div>
          </div>
        </div>

        <div className="glass rounded-2xl p-5" data-testid="settings-control">
          <div className="mb-3 flex items-center gap-2 text-sm font-medium text-white"><ShieldCheck size={16} className="text-[#00E5FF]" /> Human control</div>
          <p className="text-sm text-slate-300">Every AI associate operates at one of three authority levels — Observe, Prepare, or Act — and sensitive decisions always route to the Approval Center. You remain in control and everything is logged.</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button onClick={() => reset()} className="rounded-lg border border-white/10 px-3 py-2 text-sm text-slate-200 hover:border-[#00E5FF]/40" data-testid="settings-reset">Reset demo data</button>
            <button onClick={async () => { try { await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" }); } catch (_) {} try { localStorage.removeItem("iq-guest"); } catch (_) {} toast.info("Signed out"); navigate("/"); }} className="flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-2 text-sm text-slate-200 hover:border-[#FF0055]/40" data-testid="settings-signout"><LogOut size={14} /> Sign out</button>
          </div>
        </div>

        <div className="glass rounded-2xl p-5 lg:col-span-2" data-testid="settings-sound">
          <div className="mb-3 flex items-center gap-2 text-sm font-medium text-white"><Volume2 size={16} className="text-[#00E5FF]" /> Office ambience</div>
          <p className="text-sm text-slate-300">Set how loud the background office soundscape plays on your Headquarters. Turn it on or off with the "Sounds" button on the HQ page.</p>
          <div className="mt-4 flex items-center gap-4">
            <span className="text-xs text-slate-500">Volume</span>
            <input
              type="range"
              min="0"
              max="100"
              value={Math.round(volume * 100)}
              onChange={(e) => { const v = Number(e.target.value) / 100; setVolume(v); setAmbienceVolume(v); }}
              className="w-56 cursor-pointer accent-[#00E5FF]"
              aria-label="Office ambience volume"
              data-testid="ambience-volume-slider"
            />
            <span className="w-10 text-right font-mono text-xs text-slate-300" data-testid="ambience-volume-value">{Math.round(volume * 100)}%</span>
          </div>
        </div>

        <div className="glass rounded-2xl p-5 lg:col-span-2" data-testid="settings-phone">
          <div className="mb-1 flex items-center gap-2 text-sm font-medium text-white"><Phone size={16} className="text-[#00E5FF]" /> Customer Support phone line <span className="rounded-full border border-[#00E5FF]/30 bg-[#00E5FF]/10 px-2 py-0.5 text-[10px] font-mono uppercase text-[#00E5FF]">Nora answers</span></div>
          <p className="mb-4 text-sm text-slate-400">Connect a Twilio number so Nora answers real inbound customer support calls with her live AI voice.</p>
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3 rounded-xl border border-white/8 bg-[#030A16]/40 px-4 py-3">
              <div><div className="text-[11px] uppercase tracking-wide text-slate-500">Support number</div><div className="font-mono text-white" data-testid="phone-number">{phone?.phone_number || "Not set"}</div></div>
              <div className={`rounded-full px-2 py-0.5 text-[10px] font-mono uppercase ${phone?.openai_ready ? "bg-[#00FF88]/15 text-[#00FF88]" : "bg-[#FF0055]/15 text-[#FF3B5C]"}`}>{phone?.openai_ready ? "voice ready" : "voice not configured"}</div>
            </div>
            <div className="rounded-xl border border-white/8 bg-[#030A16]/40 px-4 py-3">
              <div className="mb-1 text-[11px] uppercase tracking-wide text-slate-500">Voice webhook (paste into Twilio)</div>
              <div className="flex items-center gap-2">
                <code className="min-w-0 flex-1 truncate font-mono text-xs text-[#00E5FF]" data-testid="phone-webhook">{webhookUrl}</code>
                <button onClick={() => copy(webhookUrl)} className="shrink-0 rounded-lg border border-white/10 px-2 py-1.5 text-slate-300 hover:border-[#00E5FF]/40 hover:text-white" data-testid="phone-copy-webhook" title="Copy"><Copy size={14} /></button>
              </div>
            </div>
            <div className="rounded-xl border border-white/8 bg-[#030A16]/40 px-4 py-3">
              <div className="mb-1 text-[11px] uppercase tracking-wide text-slate-500">Messaging webhook — SMS (paste into Twilio)</div>
              <div className="flex items-center gap-2">
                <code className="min-w-0 flex-1 truncate font-mono text-xs text-[#00E5FF]" data-testid="phone-sms-webhook">{smsWebhookUrl}</code>
                <button onClick={() => copy(smsWebhookUrl)} className="shrink-0 rounded-lg border border-white/10 px-2 py-1.5 text-slate-300 hover:border-[#00E5FF]/40 hover:text-white" data-testid="phone-copy-sms" title="Copy"><Copy size={14} /></button>
              </div>
            </div>
            <div className="rounded-xl border border-white/8 bg-[#030A16]/40 px-4 py-3 text-xs text-slate-400">
              <div className="mb-1 font-medium text-slate-300">How to connect (one time):</div>
              <ol className="list-decimal space-y-1 pl-4">
                <li>In Twilio Console → Phone Numbers → your <span className="font-mono text-slate-200">{phone?.phone_number || "+1 470 765 1212"}</span> number.</li>
                <li>Under <b>Voice</b> → "A call comes in", choose <b>Webhook</b>, method <b>HTTP POST</b>, paste the Voice URL above.</li>
                <li>Under <b>Messaging</b> → "A message comes in", choose <b>Webhook</b>, method <b>HTTP POST</b>, paste the Messaging URL above.</li>
                <li>Save. Call or text the number — Nora responds live.</li>
              </ol>
              <div className="mt-2 text-[11px] text-slate-500">Outbound: {phone?.outbound_ready ? <span className="text-[#00FF88]">connected</span> : <span className="text-[#FF3B5C]">add Twilio SID + token</span>}. Tip: ask Nora on a call to "call [number]" and she'll dial out. Use your Published site for reliable live calls.</div>
            </div>
          </div>
        </div>

        <div className="glass rounded-2xl p-5 lg:col-span-2" data-testid="settings-voices">
          <div className="mb-1 flex items-center gap-2 text-sm font-medium text-white"><Mic size={16} className="text-[#00E5FF]" /> Associate voices</div>
          <p className="mb-4 text-sm text-slate-400">Fine-tune how each associate sounds on voice calls. All voices speak neutral American English.</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {(state.employees || []).map((e) => (
              <div key={e.id} className="flex items-center gap-3 rounded-xl border border-white/8 bg-[#030A16]/40 p-3" data-testid={`voice-row-${e.id}`}>
                <EmployeeAvatar employee={e} size={38} showStatus={false} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-white">{e.name}</div>
                  <div className="truncate text-[11px] text-slate-400">{e.title}</div>
                </div>
                <select
                  value={voices[e.id] || getVoiceFor(e.id)}
                  onChange={(ev) => changeVoice(e.id, ev.target.value)}
                  className="h-9 shrink-0 rounded-lg border border-white/10 bg-[#030A16]/80 px-2 text-xs text-white focus:border-[#00E5FF]/50"
                  data-testid={`voice-select-${e.id}`}
                >
                  {VOICE_OPTIONS.map((v) => (
                    <option key={v.id} value={v.id}>{v.label}</option>
                  ))}
                </select>
                <button
                  onClick={() => previewVoice(e.id, voices[e.id] || getVoiceFor(e.id))}
                  disabled={previewing === e.id}
                  className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-white/10 text-[#00E5FF] transition-colors hover:border-[#00E5FF]/50 hover:bg-[#00E5FF]/10 disabled:opacity-60"
                  title="Hear a sample"
                  aria-label={`Preview ${e.name} voice`}
                  data-testid={`voice-preview-${e.id}`}
                >
                  {previewing === e.id ? <Loader2 size={15} className="animate-spin" /> : <Play size={15} />}
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="glass rounded-2xl p-5 lg:col-span-2" data-testid="settings-integrations">
          <div className="mb-4 flex items-center gap-2 text-sm font-medium text-white"><Plug size={16} className="text-[#00E5FF]" /> Integrations <span className="rounded-full border border-white/10 px-2 py-0.5 text-[10px] font-mono uppercase text-slate-500">Prototype placeholders</span></div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {INTEGRATIONS.map((it) => (
              <div key={it.name} className="flex items-start gap-3 rounded-xl border border-white/8 bg-[#030A16]/40 p-4">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-[#8A2BE2]/12 text-[#C4A7F5]"><it.icon size={18} /></span>
                <div className="flex-1"><div className="text-sm text-white">{it.name}</div><div className="text-xs text-slate-400">{it.desc}</div>
                  <button onClick={() => toast.info(`${it.name}`, { description: "This is a prototype placeholder — real integration connects here." })} className="mt-2 text-[11px] font-mono text-[#00E5FF]">{it.status} →</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
